import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
  Mic, Upload, FileAudio, Loader2, ChevronRight, Languages,
  Activity, CheckCircle2, AlertCircle, ArrowLeft, Cpu, Settings2,
  BarChart3, Server, Shield, FlaskConical,
} from "lucide-react";
import axios from "axios";
import { useAuth } from "@/App";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LANG_GROUPS, LANG_FLAGS as FLAGS, LANG_NAMES, GROUP_COLORS } from "@/config/languages";
import { useSpeechSynthesis } from "@/components/VoiceController";

const GROUP_ORDER = ["Global", "African", "Mzansi"];

const API = process.env.REACT_APP_BACKEND_URL;

const getHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

function StatusBadge({ status }) {
  if (status === "loading") return <Badge variant="secondary" className="gap-1"><Loader2 className="w-3 h-3 animate-spin" />Processing…</Badge>;
  if (status === "done") return <Badge className="gap-1 bg-emerald-600"><CheckCircle2 className="w-3 h-3" />Done</Badge>;
  if (status === "error") return <Badge variant="destructive" className="gap-1"><AlertCircle className="w-3 h-3" />Error</Badge>;
  return null;
}

function MetricCard({ label, value, unit, isBest }) {
  return (
    <div className={`bg-gray-800/80 rounded-lg p-3 ${isBest ? 'ring-1 ring-emerald-500/50' : ''}`}>
      <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">{label}</p>
      <p className={`text-lg font-semibold ${isBest ? 'text-emerald-400' : 'text-white'}`}>
        {value}{unit || ""}
      </p>
    </div>
  );
}

export default function AssemblyHub() {
  const navigate = useNavigate();
  const auth = useAuth();
  const isAdmin = auth.user?.role === "admin";

  // Tabs
  const [activeTab, setActiveTab] = useState("transcribe");

  // File transcription
  const [file, setFile] = useState(null);
  const [language, setLanguage] = useState("auto");
  const [fileStatus, setFileStatus] = useState("idle");
  const [transcript, setTranscript] = useState(null);
  const [backendInfo, setBackendInfo] = useState(null);

  // Compare
  const [compareFile, setCompareFile] = useState(null);
  const [compareLang, setCompareLang] = useState("auto");
  const [compareStatus, setCompareStatus] = useState("idle");
  const [compareResults, setCompareResults] = useState(null);

  // Evaluate transcript
  const [refText, setRefText] = useState("");
  const [hypText, setHypText] = useState("");
  const [evalStatus, setEvalStatus] = useState("idle");
  const [evalResult, setEvalResult] = useState(null);

  // Extract entities
  const [entityText, setEntityText] = useState("");
  const [domain, setDomain] = useState("");
  const [entityStatus, setEntityStatus] = useState("idle");
  const [entities, setEntities] = useState(null);

  // Admin: Backends
  const [backends, setBackends] = useState([]);
  const [backendStatus, setBackendStatus] = useState([]);
  const [healthLoading, setHealthLoading] = useState(false);

  // Admin: Benchmark
  const [benchFiles, setBenchFiles] = useState([]);
  const [benchRefs, setBenchRefs] = useState("");
  const [benchDomain, setBenchDomain] = useState("general");
  const [benchStatus, setBenchStatus] = useState("idle");
  const [benchRunId, setBenchRunId] = useState(null);
  const [benchResults, setBenchResults] = useState(null);
  const [benchRecommendation, setBenchRecommendation] = useState(null);

  // Transcription Provider toggle
  const [transcriptionProvider, setTranscriptionProvider] = useState("local");
  const [assemblyaiConfigured, setAssemblyaiConfigured] = useState(false);

  // Live transcription state
  const [liveSourceLang, setLiveSourceLang] = useState("auto");
  const [liveTargetLang, setLiveTargetLang] = useState("off");
  const [liveIsRecording, setIsLiveRecording] = useState(false);
  const [liveStatus, setLiveStatus] = useState("idle");
  const [liveSegments, setLiveSegments] = useState([]);
  const [livePartialText, setLivePartialText] = useState("");
  const [livePartialTranslation, setLivePartialTranslation] = useState("");
  const [liveTtsEnabled, setLiveTtsEnabled] = useState(false);
  const [liveQcEnabled, setLiveQcEnabled] = useState(true);
  const liveWsRef = useRef(null);
  const liveStreamRef = useRef(null);
  const liveAudioCtxRef = useRef(null);
  const liveProcessorRef = useRef(null);
  const liveSegmentsRef = useRef([]);
  const liveTargetLangRef = useRef(liveTargetLang);
  const liveSourceLangRef = useRef(liveSourceLang);
  const liveTtsEnabledRef = useRef(liveTtsEnabled);
  const liveQcEnabledRef = useRef(liveQcEnabled);
  const { voices, speaking, speak, stop: stopTts } = useSpeechSynthesis();

  // Keep live refs in sync
  useEffect(() => { liveTargetLangRef.current = liveTargetLang; }, [liveTargetLang]);
  useEffect(() => { liveSourceLangRef.current = liveSourceLang; }, [liveSourceLang]);
  useEffect(() => { liveTtsEnabledRef.current = liveTtsEnabled; }, [liveTtsEnabled]);
  useEffect(() => { liveQcEnabledRef.current = liveQcEnabled; }, [liveQcEnabled]);

  const liveGetWSUrl = useCallback(() => {
    const base = API || "";
    const wsBase = base.replace(/^http/, "ws");
    return `${wsBase}/ws/assembly/live`;
  }, []);

  const liveStartRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      liveStreamRef.current = stream;

      setLiveStatus("connecting");
      liveSegmentsRef.current = [];
      setLiveSegments([]);
      setLivePartialText("");
      setLivePartialTranslation("");

      const audioCtx = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: 16000 });
      const sr = audioCtx.sampleRate;

      const ws = new WebSocket(liveGetWSUrl());
      liveWsRef.current = ws;

      ws.onopen = () => {
        const token = localStorage.getItem("token") || "";
        ws.send(JSON.stringify({
          type: "config",
          sample_rate: sr,
          source_language: liveSourceLangRef.current,
          target_language: liveTargetLangRef.current === "off" ? "" : liveTargetLangRef.current,
          quality_control: liveQcEnabledRef.current,
          tts_enabled: liveTtsEnabledRef.current,
          token,
        }));
        setLiveStatus("recording");
        setIsLiveRecording(true);
        toast.success("Assembly Hub Live listening...");
      };

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          if (msg.type === "transcript") {
            const seg = {
              id: `live_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
              text: msg.text,
              raw_text: msg.raw_text || "",
              translation: msg.translation || "",
              speaker: msg.speaker || "Speaker 1",
              is_final: true,
              confidence: msg.confidence,
              quality_control: msg.quality_control || null,
              tts_ready: msg.tts_ready || false,
            };
            liveSegmentsRef.current = [...liveSegmentsRef.current, seg];
            setLiveSegments(liveSegmentsRef.current);
            setLivePartialText("");
            setLivePartialTranslation("");

            // Auto-TTS
            if (liveTtsEnabledRef.current && seg.text && !speaking) {
              const ttsText = seg.translation || seg.text;
              const ttsLang = msg.target_language || "en";
              const langVoice = voices.find(v => v.lang && v.lang.startsWith(ttsLang.slice(0, 2))) || null;
              speak(ttsText, { voice: langVoice, rate: 1.0 });
            }
          } else if (msg.type === "partial") {
            setLivePartialText(msg.text || "");
          } else if (msg.type === "session_started") {
            // Start audio capture
            try {
              const source = audioCtx.createMediaStreamSource(stream);
              const processor = audioCtx.createScriptProcessor(4096, 1, 1);
              liveAudioCtxRef.current = audioCtx;
              liveProcessorRef.current = processor;
              processor.onaudioprocess = (event) => {
                if (ws.readyState !== WebSocket.OPEN) return;
                const inputData = event.inputBuffer.getChannelData(0);
                const pcm = new Int16Array(inputData.length);
                for (let i = 0; i < inputData.length; i++) {
                  const s = Math.max(-1, Math.min(1, inputData[i]));
                  pcm[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
                }
                ws.send(pcm.buffer);
              };
              source.connect(processor);
              processor.connect(audioCtx.destination);
            } catch (err) {
              toast.error("Audio capture setup failed");
            }
          } else if (msg.type === "completed") {
            setLiveStatus("completed");
            toast.success("Transcription complete");
          } else if (msg.type === "error") {
            toast.error(msg.detail || "Live transcription error");
            setIsLiveRecording(false);
            setLiveStatus("error");
          }
        } catch {}
      };

      ws.onerror = () => {
        toast.error("WebSocket connection failed");
        setIsLiveRecording(false);
        setLiveStatus("error");
      };

      ws.onclose = () => {
        if (liveWsRef.current === ws) setIsLiveRecording(false);
      };
    } catch (err) {
      toast.error("Could not access microphone");
      setLiveStatus("idle");
    }
  }, [liveGetWSUrl, voices, speaking, speak]);

  const liveStopRecording = useCallback(() => {
    stopTts();
    if (liveWsRef.current?.readyState === WebSocket.OPEN) {
      liveWsRef.current.send(JSON.stringify({ type: "stop" }));
    }
    setIsLiveRecording(false);
    setLiveStatus("completed");
    // Cleanup audio
    if (liveProcessorRef.current && liveAudioCtxRef.current) {
      try { liveProcessorRef.current.disconnect(); } catch {}
    }
    if (liveAudioCtxRef.current) {
      try { liveAudioCtxRef.current.close(); } catch {}
    }
    if (liveStreamRef.current) {
      liveStreamRef.current.getTracks().forEach(t => t.stop());
    }
    if (liveWsRef.current) {
      try { liveWsRef.current.close(); } catch {}
    }
  }, [stopTts]);

  const fileInputRef = useRef(null);
  const compareInputRef = useRef(null);
  const benchInputRef = useRef(null);

  // ── Fetch backend config (admin) ──
  const fetchBackends = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/assembly/admin/backends`, {
        headers: getHeaders(), withCredentials: true,
      });
      setBackends(res.data.backends || []);
    } catch {}
  }, []);

  const fetchTranscriptionProvider = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/assembly/admin/transcription-provider`, {
        headers: getHeaders(), withCredentials: true,
      });
      setTranscriptionProvider(res.data.provider);
      setAssemblyaiConfigured(res.data.assemblyai_configured);
    } catch {}
  }, []);

  const handleSetTranscriptionProvider = async (provider) => {
    try {
      await axios.put(`${API}/api/assembly/admin/transcription-provider`,
        { provider },
        { headers: getHeaders(), withCredentials: true },
      );
      setTranscriptionProvider(provider);
      toast.success(`Transcription provider: ${provider}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to set provider");
    }
  };

  // ── File transcription ──
  const handleTranscribeFile = async () => {
    if (!file) return toast.error("Select an audio file first");
    setFileStatus("loading");
    setTranscript(null);
    setBackendInfo(null);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("language_code", language === "auto" ? "" : language);
      const res = await axios.post(`${API}/api/assembly/transcribe`, form, {
        headers: getHeaders(), withCredentials: true,
      });
      setTranscript(res.data);
      setBackendInfo({ used: res.data.backend_used, chain: res.data.fallback_chain });
      setFileStatus("done");
      toast.success(`Transcribed with ${res.data.backend_used}`);
    } catch (err) {
      toast.error(`Transcription failed: ${err.response?.data?.detail || err.message}`);
      setFileStatus("error");
    }
  };

  // ── Compare all backends ──
  const handleCompare = async () => {
    if (!compareFile) return toast.error("Select an audio file first");
    setCompareStatus("loading");
    setCompareResults(null);
    try {
      const form = new FormData();
      form.append("file", compareFile);
      form.append("language_code", compareLang === "auto" ? "" : compareLang);
      const res = await axios.post(`${API}/api/assembly/transcribe/compare`, form, {
        headers: getHeaders(), withCredentials: true,
      });
      setCompareResults(res.data.results);
      setCompareStatus("done");
    } catch (err) {
      toast.error(`Comparison failed: ${err.response?.data?.detail || err.message}`);
      setCompareStatus("error");
    }
  };

  // ── Evaluate transcript ──
  const handleEvaluate = async () => {
    if (!refText.trim() || !hypText.trim()) return toast.error("Enter both reference and hypothesis text");
    setEvalStatus("loading");
    setEvalResult(null);
    try {
      const res = await axios.post(`${API}/api/assembly/evaluate`, {
        reference: refText, hypothesis: hypText,
      }, {
        headers: { ...getHeaders(), "Content-Type": "application/json" },
        withCredentials: true,
      });
      setEvalResult(res.data);
      setEvalStatus("done");
    } catch (err) {
      toast.error(`Evaluation failed: ${err.response?.data?.detail || err.message}`);
      setEvalStatus("error");
    }
  };

  // ── Extract entities ──
  const handleExtractEntities = async () => {
    if (!entityText.trim()) return toast.error("Enter transcript text");
    setEntityStatus("loading");
    setEntities(null);
    try {
      const body = { text: entityText };
      if (domain) body.domain = domain;
      const res = await axios.post(`${API}/api/assembly/extract-entities`, body, {
        headers: { ...getHeaders(), "Content-Type": "application/json" },
        withCredentials: true,
      });
      setEntities(res.data);
      setEntityStatus("done");
    } catch (err) {
      toast.error(`Entity extraction failed: ${err.response?.data?.detail || err.message}`);
      setEntityStatus("error");
    }
  };

  // ── Admin: Toggle backend ──
  const handleToggleBackend = async (name, enabled) => {
    try {
      await axios.put(`${API}/api/assembly/admin/backends/${name}/enable`,
        { enabled },
        { headers: getHeaders(), withCredentials: true },
      );
      toast.success(`${name} ${enabled ? "enabled" : "disabled"}`);
      fetchBackends();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to toggle backend");
    }
  };

  // ── Admin: Set primary ──
  const handleSetPrimary = async (name) => {
    try {
      await axios.put(`${API}/api/assembly/admin/backends/primary/${name}`, {}, {
        headers: getHeaders(), withCredentials: true,
      });
      toast.success(`${name} set as primary`);
      fetchBackends();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to set primary");
    }
  };

  // ── Admin: Health check ──
  const handleHealthCheck = async () => {
    setHealthLoading(true);
    try {
      const res = await axios.get(`${API}/api/assembly/admin/backends/status`, {
        headers: getHeaders(), withCredentials: true,
      });
      setBackendStatus(res.data.status || []);
    } catch (err) {
      toast.error("Health check failed");
    }
    setHealthLoading(false);
  };

  // ── Admin: Benchmark upload ──
  const handleUploadBenchmark = async () => {
    if (benchFiles.length === 0) return toast.error("Select at least one audio file");
    const refs = benchRefs.split("\n").filter(r => r.trim());
    if (refs.length !== benchFiles.length) return toast.error(`Reference count (${refs.length}) must match file count (${benchFiles.length})`);

    setBenchStatus("loading");
    try {
      const form = new FormData();
      benchFiles.forEach(f => form.append("files", f));
      form.append("references", JSON.stringify(refs));
      form.append("domain", benchDomain);
      form.append("run_name", `Benchmark ${new Date().toLocaleString()}`);
      const res = await axios.post(`${API}/api/assembly/admin/benchmark/upload`, form, {
        headers: getHeaders(), withCredentials: true,
      });
      setBenchRunId(res.data.run_id);
      toast.success(`Benchmark started: ${res.data.total_files} files`);

      // Poll for completion
      const poll = setInterval(async () => {
        try {
          const r = await axios.get(`${API}/api/assembly/admin/benchmark/${res.data.run_id}`, {
            headers: getHeaders(), withCredentials: true,
          });
          if (r.data.run?.status === "completed") {
            clearInterval(poll);
            setBenchResults(r.data);
            setBenchStatus("done");
            // Also get recommendation
            const rec = await axios.post(`${API}/api/assembly/admin/benchmark/apply`,
              { run_id: res.data.run_id },
              { headers: getHeaders(), withCredentials: true },
            );
            setBenchRecommendation(rec.data);
            toast.success("Benchmark complete!");
          }
        } catch {
          clearInterval(poll);
          setBenchStatus("error");
        }
      }, 3000);
    } catch (err) {
      toast.error(`Benchmark upload failed: ${err.response?.data?.detail || err.message}`);
      setBenchStatus("error");
    }
  };

  // ── Admin: Load latest benchmark ──
  const handleLoadLatestBenchmark = async () => {
    try {
      const res = await axios.get(`${API}/api/assembly/admin/benchmark/latest`, {
        headers: getHeaders(), withCredentials: true,
      });
      if (res.data.run) {
        setBenchResults(res.data);
        setBenchStatus("done");
        toast.success("Loaded latest benchmark");
      } else {
        toast.info("No benchmark runs found");
      }
    } catch {
      toast.error("Failed to load latest benchmark");
    }
  };

  // ── Admin: Apply recommendation ──
  const handleApplyRecommendation = async () => {
    if (!benchResults?.run?.run_id) return;
    try {
      const res = await axios.post(`${API}/api/assembly/admin/benchmark/apply`,
        { run_id: benchResults.run.run_id },
        { headers: getHeaders(), withCredentials: true },
      );
      toast.success(`Applied: primary = ${res.data.primary_backend}`);
      fetchBackends();
    } catch (err) {
      toast.error("Failed to apply recommendation");
    }
  };

  // Fetch backends on admin tab mount
  useEffect(() => {
    if (isAdmin) { fetchBackends(); fetchTranscriptionProvider(); }
  }, [isAdmin, fetchBackends, fetchTranscriptionProvider]);

  // ── Render ──
  return (
    <div className="min-h-screen bg-gray-950 text-gray-100">
      {/* Header */}
      <div className="border-b border-gray-800 bg-gray-900/60 backdrop-blur px-4 py-3 flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard")} className="text-gray-400 hover:text-white">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <Cpu className="w-6 h-6 text-green-400" />
        <div>
          <h1 className="font-semibold text-white leading-tight">Assembly Hub</h1>
          <p className="text-xs text-gray-400">High-accuracy multi-ASR transcription &amp; evaluation</p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="max-w-5xl mx-auto px-4 pt-4">
        <TabsList className="bg-gray-900 border border-gray-800">
          <TabsTrigger value="transcribe" className="data-[state=active]:bg-gray-800">
            <Mic className="w-4 h-4 mr-1.5" /> Transcribe
          </TabsTrigger>
          <TabsTrigger value="compare" className="data-[state=active]:bg-gray-800">
            <BarChart3 className="w-4 h-4 mr-1.5" /> Compare
          </TabsTrigger>
          <TabsTrigger value="evaluate" className="data-[state=active]:bg-gray-800">
            <CheckCircle2 className="w-4 h-4 mr-1.5" /> Evaluate
          </TabsTrigger>
          <TabsTrigger value="entities" className="data-[state=active]:bg-gray-800">
            <Activity className="w-4 h-4 mr-1.5" /> Entities
          </TabsTrigger>
          <TabsTrigger value="live" className="data-[state=active]:bg-gray-800">
            <Cpu className="w-4 h-4 mr-1.5" /> Live
          </TabsTrigger>
          {isAdmin && (
            <TabsTrigger value="admin" className="data-[state=active]:bg-gray-800">
              <Shield className="w-4 h-4 mr-1.5" /> Admin
            </TabsTrigger>
          )}
        </TabsList>

        {/* ── Transcribe Tab ── */}
        <TabsContent value="transcribe" className="mt-4 space-y-4">
          <div className="rounded-xl border border-gray-800 bg-gray-900/50 p-5">
            <div className="flex items-center gap-2 mb-4">
              <FileAudio className="w-5 h-5 text-green-400" />
              <h2 className="font-medium text-white">Transcribe Audio File</h2>
              <StatusBadge status={fileStatus} />
            </div>

            <div
              className="border-2 border-dashed border-gray-700 rounded-lg p-6 text-center cursor-pointer hover:border-green-600 transition-colors"
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) setFile(f); }}
            >
              <Upload className="w-8 h-8 mx-auto mb-2 text-gray-500" />
              {file ? (
                <p className="text-sm text-green-400">{file.name} ({(file.size / 1024 / 1024).toFixed(1)} MB)</p>
              ) : (
                <p className="text-sm text-gray-400">Drop an audio file here or click to browse</p>
              )}
              <input ref={fileInputRef} type="file" accept="audio/*,video/*" className="hidden" onChange={(e) => setFile(e.target.files[0])} />
            </div>

            <div className="flex gap-2 mt-3">
              <Select value={language} onValueChange={setLanguage}>
                <SelectTrigger className="flex-1 bg-gray-800 border-gray-700 text-white text-sm rounded">
                  <SelectValue placeholder="Language (optional)" />
                </SelectTrigger>
                <SelectContent className="bg-gray-900 border-gray-700 text-white">
                  <SelectItem value="auto">— Auto-detect —</SelectItem>
                  {GROUP_ORDER.map(group => {
                    const codes = LANG_GROUPS[group];
                    if (!codes) return null;
                    return (
                    <div key={group}>
                      <div className={`px-2 py-1 text-[10px] uppercase font-bold tracking-wider ${GROUP_COLORS[group]?.text || 'text-gray-500'}`}>
                        <span className={`inline-block px-1.5 py-0.5 rounded ${GROUP_COLORS[group]?.bg || ''} ${GROUP_COLORS[group]?.border ? 'border ' + GROUP_COLORS[group].border : ''}`}>
                          {group}
                        </span>
                      </div>
                      {codes.map(code => (
                        <SelectItem key={code} value={code} className="text-sm">
                          {FLAGS[code] || "🌐"} {LANG_NAMES[code] || code}
                        </SelectItem>
                      ))}
                    </div>
                  );
                })}
                </SelectContent>
              </Select>
              <Button onClick={handleTranscribeFile} disabled={!file || fileStatus === "loading"} className="bg-green-600 hover:bg-green-700 text-white">
                {fileStatus === "loading" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mic className="w-4 h-4" />}
                <span className="ml-1">Transcribe</span>
              </Button>
            </div>

            {backendInfo && (
              <p className="text-xs text-gray-500 mt-2">
                Used backend: <span className="text-green-400">{backendInfo.used}</span>
                {backendInfo.chain?.length > 1 && ` · fallback chain: ${backendInfo.chain.join(" → ")}`}
              </p>
            )}

            {transcript && (
              <div className="mt-4 rounded-lg bg-gray-800/60 p-4 space-y-2">
                <p className="text-xs text-gray-400 uppercase tracking-wide">Transcript</p>
                <p className="text-sm text-white leading-relaxed whitespace-pre-wrap">{transcript.text || transcript.transcription || JSON.stringify(transcript)}</p>
                {transcript.language && <Badge variant="outline" className="text-xs">{transcript.language}</Badge>}
                {transcript.duration_s != null && (
                  <p className="text-xs text-gray-500">{Math.round(transcript.duration_s)}s · confidence: {transcript.confidence != null ? (transcript.confidence * 100).toFixed(1) + '%' : 'N/A'} · {transcript.processing_ms}ms</p>
                )}
              </div>
            )}
          </div>
        </TabsContent>

        {/* ── Compare Tab ── */}
        <TabsContent value="compare" className="mt-4 space-y-4">
          <div className="rounded-xl border border-gray-800 bg-gray-900/50 p-5">
            <div className="flex items-center gap-2 mb-4">
              <BarChart3 className="w-5 h-5 text-amber-400" />
              <h2 className="font-medium text-white">Compare All Backends</h2>
              <StatusBadge status={compareStatus} />
            </div>
            <p className="text-xs text-gray-400 mb-4">Upload one audio file and see how every enabled ASR backend performs side-by-side</p>

            <div
              className="border-2 border-dashed border-gray-700 rounded-lg p-6 text-center cursor-pointer hover:border-amber-600 transition-colors"
              onClick={() => compareInputRef.current?.click()}
            >
              <Upload className="w-8 h-8 mx-auto mb-2 text-gray-500" />
              {compareFile ? <p className="text-sm text-amber-400">{compareFile.name}</p> : <p className="text-sm text-gray-400">Select audio file</p>}
              <input ref={compareInputRef} type="file" accept="audio/*,video/*" className="hidden" onChange={(e) => setCompareFile(e.target.files[0])} />
            </div>

            <div className="flex gap-2 mt-3">
              <Select value={compareLang} onValueChange={setCompareLang}>
                <SelectTrigger className="flex-1 bg-gray-800 border-gray-700 text-white text-sm rounded">
                  <SelectValue placeholder="Language (optional)" />
                </SelectTrigger>
                <SelectContent className="bg-gray-900 border-gray-700 text-white">
                  <SelectItem value="auto">— Auto-detect —</SelectItem>
                  {GROUP_ORDER.map(group => {
                    const codes = LANG_GROUPS[group];
                    if (!codes) return null;
                    return (
                    <div key={group}>
                      <div className={`px-2 py-1 text-[10px] uppercase font-bold tracking-wider ${GROUP_COLORS[group]?.text || 'text-gray-500'}`}>
                        <span className={`inline-block px-1.5 py-0.5 rounded ${GROUP_COLORS[group]?.bg || ''} ${GROUP_COLORS[group]?.border ? 'border ' + GROUP_COLORS[group].border : ''}`}>
                          {group}
                        </span>
                      </div>
                      {codes.map(code => (
                        <SelectItem key={code} value={code} className="text-sm">
                          {FLAGS[code] || "🌐"} {LANG_NAMES[code] || code}
                        </SelectItem>
                      ))}
                    </div>
                  );
                })}
                </SelectContent>
              </Select>
              <Button onClick={handleCompare} disabled={!compareFile || compareStatus === "loading"} className="bg-amber-600 hover:bg-amber-700 text-white">
                {compareStatus === "loading" ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <BarChart3 className="w-4 h-4 mr-1" />}
                Compare
              </Button>
            </div>

            {compareResults && (
              <div className="mt-4 space-y-4">
                {Object.entries(compareResults).map(([name, data]) => (
                  <div key={name} className="rounded-lg bg-gray-800/60 p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge className="bg-amber-600/20 text-amber-400 border border-amber-600/30">{name}</Badge>
                      {data.error && <Badge variant="destructive">Error</Badge>}
                    </div>
                    {data.error ? (
                      <p className="text-sm text-red-400">{data.error}</p>
                    ) : (
                      <>
                        <p className="text-sm text-white whitespace-pre-wrap">{data.text || "(no text)"}</p>
                        <div className="flex gap-3 mt-2 text-xs text-gray-400">
                          {data.processing_ms != null && <span>⏱ {data.processing_ms}ms</span>}
                          {data.confidence != null && <span>🎯 {(data.confidence * 100).toFixed(1)}%</span>}
                          {data.language && <span>🌐 {data.language}</span>}
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        {/* ── Evaluate Tab ── */}
        <TabsContent value="evaluate" className="mt-4 space-y-4">
          <div className="rounded-xl border border-gray-800 bg-gray-900/50 p-5">
            <div className="flex items-center gap-2 mb-4">
              <CheckCircle2 className="w-5 h-5 text-blue-400" />
              <h2 className="font-medium text-white">Evaluate Transcript (WER)</h2>
              <StatusBadge status={evalStatus} />
            </div>

            <div className="space-y-2">
              <textarea placeholder="Reference text (ground truth)" value={refText} onChange={(e) => setRefText(e.target.value)}
                rows={3} className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-white placeholder-gray-500 resize-none focus:outline-none focus:border-blue-500" />
              <textarea placeholder="Hypothesis text (transcription output)" value={hypText} onChange={(e) => setHypText(e.target.value)}
                rows={3} className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-white placeholder-gray-500 resize-none focus:outline-none focus:border-blue-500" />
              <Button onClick={handleEvaluate} disabled={evalStatus === "loading"} className="bg-blue-600 hover:bg-blue-700 text-white">
                {evalStatus === "loading" ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <ChevronRight className="w-4 h-4 mr-1" />}
                Evaluate
              </Button>
            </div>

            {evalResult && (
              <div className="mt-4 rounded-lg bg-gray-800/60 p-4 grid grid-cols-2 gap-3">
                {Object.entries(evalResult).map(([k, v]) => (
                  typeof v === "number" ? (
                    <div key={k} className="bg-gray-800 rounded p-3">
                      <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">{k.replace(/_/g, " ")}</p>
                      <p className="text-lg font-semibold text-white">
                        {(v * (k.toLowerCase().includes("rate") || k.toLowerCase().includes("wer") || k.toLowerCase().includes("score") ? 100 : 1)).toFixed(1)}
                        {k.toLowerCase().includes("rate") || k.toLowerCase().includes("wer") || k.toLowerCase().includes("score") ? "%" : ""}
                      </p>
                    </div>
                  ) : null
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        {/* ── Entities Tab ── */}
        <TabsContent value="entities" className="mt-4 space-y-4">
          <div className="rounded-xl border border-gray-800 bg-gray-900/50 p-5">
            <div className="flex items-center gap-2 mb-4">
              <Activity className="w-5 h-5 text-purple-400" />
              <h2 className="font-medium text-white">Extract Domain Entities</h2>
              <StatusBadge status={entityStatus} />
            </div>

            <div className="space-y-2">
              <textarea placeholder="Paste transcript text…" value={entityText} onChange={(e) => setEntityText(e.target.value)}
                rows={4} className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-white placeholder-gray-500 resize-none focus:outline-none focus:border-purple-500" />
              <div className="flex gap-2">
                <input type="text" placeholder="Domain (e.g. medical, legal) — optional" value={domain} onChange={(e) => setDomain(e.target.value)}
                  className="flex-1 bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-purple-500" />
                <Button onClick={handleExtractEntities} disabled={entityStatus === "loading"} className="bg-purple-600 hover:bg-purple-700 text-white">
                  {entityStatus === "loading" ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <ChevronRight className="w-4 h-4 mr-1" />}
                  Extract
                </Button>
              </div>
            </div>

            {entities && (
              <div className="mt-4 rounded-lg bg-gray-800/60 p-4 space-y-3">
                {entities.entities?.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {entities.entities.map((e, i) => (
                      <Badge key={i} variant="outline" className="text-purple-300 border-purple-700 bg-purple-900/20">
                        {e.text ?? e.term ?? e.value ?? JSON.stringify(e)}
                        {e.type && <span className="ml-1 text-gray-500">·{e.type}</span>}
                      </Badge>
                    ))}
                  </div>
                ) : <p className="text-sm text-gray-400">No entities found.</p>}
              </div>
            )}
          </div>
        </TabsContent>

        {/* ── Live Tab ── */}
        <TabsContent value="live" className="mt-4">
          <div className="rounded-xl border border-gray-800 bg-gray-900/50 p-5">
            <div className="flex items-center gap-2 mb-4">
              <Cpu className="w-5 h-5 text-cyan-400" />
              <h2 className="font-medium text-white">Live Transcription</h2>
              {liveStatus === "recording" && <span className="text-[10px] text-red-400 animate-pulse">● REC</span>}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* Controls */}
              <div className="space-y-3">
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-gray-500 mb-1.5 block">Source Language</label>
                  <Select value={liveSourceLang} onValueChange={setLiveSourceLang}>
                    <SelectTrigger className="w-full bg-gray-800 border-gray-700 text-white text-sm">
                      <SelectValue placeholder="Auto-detect" />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-900 border-gray-700 text-white">
                      <SelectItem value="auto">🌐 Auto-detect</SelectItem>
                      {GROUP_ORDER.map(group => {
                        const codes = LANG_GROUPS[group];
                        if (!codes) return null;
                        return (
                          <div key={group}>
                            <div className={`px-2 py-1 text-[10px] uppercase font-bold tracking-wider ${GROUP_COLORS[group]?.text || 'text-gray-500'}`}>
                              {group}
                            </div>
                            {codes.map(code => (
                              <SelectItem key={code} value={code} className="text-sm">
                                {FLAGS[code] || "🌐"} {LANG_NAMES[code] || code}
                              </SelectItem>
                            ))}
                          </div>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-[10px] uppercase tracking-widest text-gray-500 mb-1.5 block">Target Language</label>
                  <Select value={liveTargetLang} onValueChange={setLiveTargetLang}>
                    <SelectTrigger className="w-full bg-gray-800 border-gray-700 text-white text-sm">
                      <SelectValue placeholder="Off" />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-900 border-gray-700 text-white">
                      <SelectItem value="off">— Off —</SelectItem>
                      {GROUP_ORDER.map(group => {
                        const codes = LANG_GROUPS[group];
                        if (!codes) return null;
                        return (
                          <div key={group}>
                            <div className={`px-2 py-1 text-[10px] uppercase font-bold tracking-wider ${GROUP_COLORS[group]?.text || 'text-gray-500'}`}>
                              {group}
                            </div>
                            {codes.map(code => (
                              <SelectItem key={code} value={code} className="text-sm">{FLAGS[code] || "🌐"} {LANG_NAMES[code] || code}</SelectItem>
                            ))}
                          </div>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>

                <div className="h-px bg-gray-800" />

                <div className="grid grid-cols-1 gap-2">
                  {!liveIsRecording ? (
                    <Button onClick={liveStartRecording} className="bg-cyan-600 hover:bg-cyan-500 text-white rounded">
                      <Mic className="w-4 h-4 mr-2" /> Start Mic
                    </Button>
                  ) : (
                    <Button onClick={liveStopRecording} className="bg-red-600 hover:bg-red-500 text-white rounded">
                      <Cpu className="w-4 h-4 mr-2" /> Stop & Process
                    </Button>
                  )}
                </div>

                <div className="h-px bg-gray-800" />

                {/* TTS & QC Toggles */}
                <div className="flex items-center justify-between bg-gray-800/60 rounded p-2.5">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-300">Voice Output</span>
                  </div>
                  <Switch checked={liveTtsEnabled} onCheckedChange={setLiveTtsEnabled}
                    className="data-[state=checked]:bg-violet-600 scale-75" />
                </div>
                <div className="flex items-center justify-between bg-gray-800/60 rounded p-2.5">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-300">Quality Control</span>
                  </div>
                  <Switch checked={liveQcEnabled} onCheckedChange={setLiveQcEnabled}
                    className="data-[state=checked]:bg-cyan-600 scale-75" />
                </div>

                <div className="text-center">
                  <span className={`inline-flex items-center gap-1.5 text-xs ${
                    liveStatus === "recording" ? "text-green-400" :
                    liveStatus === "connecting" ? "text-amber-400" :
                    liveStatus === "error" ? "text-red-400" : "text-gray-500"
                  }`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${
                      liveStatus === "recording" ? "bg-green-400 animate-pulse" :
                      liveStatus === "connecting" ? "bg-amber-400" :
                      liveStatus === "error" ? "bg-red-400" : "bg-gray-500"
                    }`} />
                    {liveStatus === "recording" ? "Listening" :
                     liveStatus === "connecting" ? "Connecting..." :
                     liveStatus === "error" ? "Error" : "Ready"}
                  </span>
                </div>
              </div>

              {/* Live Transcript */}
              <div className="lg:col-span-1 bg-gray-800/40 rounded-lg p-4 overflow-y-auto" style={{ maxHeight: "500px", minHeight: 200 }}>
                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Live Transcript</h3>
                {liveSegments.length === 0 && !liveIsRecording && liveStatus !== "recording" && liveStatus !== "connecting" && (
                  <p className="text-sm text-gray-600 text-center py-8">Press Start Mic to begin</p>
                )}
                <div className="space-y-2">
                  {liveSegments.map((seg, i) => (
                    <div key={seg.id || i} className="border-l-2 border-l-cyan-500/50 pl-3 py-1 hover:bg-gray-800/30 rounded-r">
                      {seg.speaker && (
                        <p className="text-[10px] font-semibold text-cyan-400/70 uppercase mb-0.5">{seg.speaker}</p>
                      )}
                      <p className="text-sm text-green-300">
                        {seg.text}
                        {seg.quality_control?.corrections_count > 0 && (
                          <span className="ml-2 text-[9px] text-cyan-500" title={`${seg.quality_control.corrections_count} corrections`}>✦ QC</span>
                        )}
                      </p>
                      {seg.raw_text && seg.raw_text !== seg.text && (
                        <details className="mt-0.5">
                          <summary className="text-[9px] text-gray-600 cursor-pointer">raw</summary>
                          <p className="text-[10px] text-gray-500 italic">{seg.raw_text}</p>
                        </details>
                      )}
                    </div>
                  ))}
                  {livePartialText && (
                    <div className="border-l-2 border-l-gray-600 pl-3 py-1 animate-pulse">
                      <p className="text-sm text-gray-400 italic">▌{livePartialText}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Translation Panel */}
              <div className="bg-gray-800/40 rounded-lg p-4 overflow-y-auto" style={{ maxHeight: "500px", minHeight: 200 }}>
                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
                  Translation {liveTargetLang !== "off" ? `→ ${FLAGS[liveTargetLang] || "🌐"} ${(LANG_NAMES[liveTargetLang] || liveTargetLang).toUpperCase()}` : "(off)"}
                </h3>
                {liveTargetLang === "off" ? (
                  <p className="text-sm text-gray-600 text-center py-8">Select a target language to enable translation</p>
                ) : (
                  <div className="space-y-2">
                    {liveSegments.map((seg, i) => (
                      <div key={seg.id || i} className="border-l-2 border-l-amber-600/50 pl-3 py-1 hover:bg-gray-800/30 rounded-r group">
                        {seg.speaker && (
                          <p className="text-[10px] font-semibold text-amber-400/70 uppercase mb-0.5">{seg.speaker}</p>
                        )}
                        <div className="flex items-start gap-1">
                          <p className="text-sm text-gray-100 flex-1">{seg.translation || "(translating...)"}</p>
                          {seg.translation && (
                            <button
                              onClick={() => {
                                const lv = voices.find(v => v.lang && v.lang.startsWith((liveTargetLangRef.current || "en").slice(0, 2))) || null;
                                speak(seg.translation, { voice: lv, rate: 1.0 });
                              }}
                              className="opacity-0 group-hover:opacity-100 text-amber-500 hover:text-amber-300 shrink-0 mt-0.5"
                              title="Read aloud"
                            >
                              <Cpu className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                    {livePartialTranslation && (
                      <div className="border-l-2 border-l-gray-600 pl-3 py-1 animate-pulse">
                        <p className="text-sm text-gray-400 italic">▌{livePartialTranslation}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </TabsContent>

        {/* ── Admin Tab ── */}
        {isAdmin && (
          <TabsContent value="admin" className="mt-4 space-y-6">
            {/* Backend Configuration */}
            <div className="rounded-xl border border-gray-800 bg-gray-900/50 p-5">
              <div className="flex items-center gap-2 mb-4">
                <Server className="w-5 h-5 text-cyan-400" />
                <h2 className="font-medium text-white">ASR Backend Configuration</h2>
              </div>

              <div className="space-y-4">
                {backends.map((b) => (
                  <div key={b.name} className="bg-gray-800/60 rounded-lg p-4 border border-gray-700/50">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <h3 className="text-sm font-medium text-white">{b.display_name || b.name}</h3>
                        <p className="text-xs text-gray-400">{b.description}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <label className="flex items-center gap-2 text-xs text-gray-400">
                          <Switch
                            checked={b.enabled}
                            onCheckedChange={(v) => handleToggleBackend(b.name, v)}
                          />
                          {b.enabled ? "Enabled" : "Disabled"}
                        </label>
                        {b.enabled && (
                          <label className="flex items-center gap-1.5 text-xs text-gray-400 cursor-pointer">
                            <input
                              type="radio" name="primary_backend"
                              checked={b.is_primary}
                              onChange={() => handleSetPrimary(b.name)}
                              className="text-cyan-500"
                            />
                            Primary
                          </label>
                        )}
                      </div>
                    </div>
                    {b.is_primary && <Badge className="bg-cyan-600/20 text-cyan-400 border border-cyan-600/30 text-[10px]">Primary</Badge>}
                    <div className="mt-2 text-xs text-gray-500">
                      Fallback order: {b.fallback_order} · Config: {JSON.stringify(b.config || {})}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Transcription Provider Toggle */}
            <div className="rounded-xl border border-gray-800 bg-gray-900/50 p-5">
              <div className="flex items-center gap-2 mb-4">
                <Cpu className="w-5 h-5 text-cyan-400" />
                <h2 className="font-medium text-white">Transcription Provider</h2>
              </div>
              <p className="text-xs text-gray-400 mb-3">
                Choose between local Whisper/GPU models or AssemblyAI cloud API for speech-to-text.
                {assemblyaiConfigured ? "" : " ASSEMBLYAI_API_KEY not set on Railway."}
              </p>
              <div className="flex gap-3">
                <Button
                  onClick={() => handleSetTranscriptionProvider("local")}
                  className={`${transcriptionProvider === "local" ? "bg-cyan-600" : "bg-gray-800"} hover:bg-cyan-500 text-white text-sm`}
                >
                  <Cpu className="w-4 h-4 mr-1.5" /> Local / GPU
                </Button>
                <Button
                  onClick={() => handleSetTranscriptionProvider("assemblyai")}
                  disabled={!assemblyaiConfigured}
                  className={`${transcriptionProvider === "assemblyai" ? "bg-green-600" : "bg-gray-800"} hover:bg-green-500 text-white text-sm`}
                >
                  <Cpu className="w-4 h-4 mr-1.5" /> AssemblyAI Cloud
                </Button>
              </div>
              <p className="text-xs text-gray-600 mt-2">
                Current: <span className="text-cyan-400 font-medium">{transcriptionProvider}</span>
                {!assemblyaiConfigured && <span className="text-amber-400"> — Set ASSEMBLYAI_API_KEY env var to enable</span>}
              </p>
            </div>

            {/* Health Check */}
            <div className="rounded-xl border border-gray-800 bg-gray-900/50 p-5">
              <div className="flex items-center gap-2 mb-4">
                <Activity className="w-5 h-5 text-green-400" />
                <h2 className="font-medium text-white">Health Check</h2>
              </div>
              <Button onClick={handleHealthCheck} disabled={healthLoading} className="bg-green-600 hover:bg-green-700 text-white mb-3">
                {healthLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Activity className="w-4 h-4 mr-1" />}
                Run Health Check
              </Button>

            {/* Health Check results */}
            {backendStatus.length > 0 && (
                <div className="space-y-2">
                  {backendStatus.map((s) => (
                    <div key={s.name} className="flex items-center justify-between bg-gray-800/60 rounded p-3">
                      <div className="flex items-center gap-2">
                        <span className={s.healthy ? "text-green-400" : "text-red-400"}>
                          {s.healthy ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                        </span>
                        <span className="text-sm text-white">{s.name}</span>
                        <Badge variant="outline" className={`text-[10px] ${s.loaded ? 'text-green-400 border-green-700' : 'text-red-400 border-red-700'}`}>
                          {s.loaded ? "Loaded" : "Not loaded"}
                        </Badge>
                      </div>
                      {s.error && <span className="text-xs text-red-400">{s.error}</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Benchmark */}
            <div className="rounded-xl border border-gray-800 bg-gray-900/50 p-5">
              <div className="flex items-center gap-2 mb-4">
                <FlaskConical className="w-5 h-5 text-violet-400" />
                <h2 className="font-medium text-white">Accuracy Benchmarking</h2>
                <StatusBadge status={benchStatus} />
              </div>
              <p className="text-xs text-gray-400 mb-4">Upload test audio files + reference transcripts to compare accuracy across all enabled backends</p>

              <div
                className="border-2 border-dashed border-gray-700 rounded-lg p-4 text-center cursor-pointer hover:border-violet-600 transition-colors mb-3"
                onClick={() => benchInputRef.current?.click()}
              >
                <Upload className="w-6 h-6 mx-auto mb-1 text-gray-500" />
                {benchFiles.length > 0
                  ? <p className="text-sm text-violet-400">{benchFiles.length} file(s) selected</p>
                  : <p className="text-sm text-gray-400">Select audio files</p>}
                <input ref={benchInputRef} type="file" accept="audio/*,video/*" multiple className="hidden"
                  onChange={(e) => setBenchFiles(Array.from(e.target.files))} />
              </div>

              <div className="space-y-2">
                <textarea placeholder="Reference transcripts (one per line, matching file order)" value={benchRefs} onChange={(e) => setBenchRefs(e.target.value)}
                  rows={3} className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-white placeholder-gray-500 resize-none focus:outline-none focus:border-violet-500" />
                <div className="flex gap-2">
                  <input type="text" placeholder="Domain (e.g. medical, legal)" value={benchDomain} onChange={(e) => setBenchDomain(e.target.value)}
                    className="flex-1 bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-violet-500" />
                  <Button onClick={handleUploadBenchmark} disabled={benchStatus === "loading" || benchFiles.length === 0} className="bg-violet-600 hover:bg-violet-700 text-white">
                    {benchStatus === "loading" ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <FlaskConical className="w-4 h-4 mr-1" />}
                    Start Benchmark
                  </Button>
                  <Button onClick={handleLoadLatestBenchmark} variant="outline" className="border-gray-600 text-gray-300">
                    Load Latest
                  </Button>
                </div>
              </div>

              {/* Benchmark Results */}
              {benchResults?.run?.results_summary && (
                <div className="mt-4 space-y-3">
                  <h3 className="text-sm font-medium text-white">Results: {benchResults.run.run_name}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {Object.entries(benchResults.run.results_summary).map(([name, data]) => (
                      <div key={name} className="bg-gray-800/80 rounded-lg p-4 border border-gray-700/50">
                        <Badge className="mb-2 bg-violet-600/20 text-violet-400 border border-violet-600/30">{name}</Badge>
                        {data.error ? (
                          <p className="text-xs text-red-400">{data.error}</p>
                        ) : (
                          <div className="space-y-1.5">
                            <MetricCard label="WER" value={(data.avg_wer * 100).toFixed(2)} unit="%" isBest={false} />
                            <MetricCard label="Accuracy" value={(data.avg_accuracy * 100).toFixed(1)} unit="%" isBest={false} />
                            <MetricCard label="Avg Time" value={data.avg_time_ms} unit="ms" isBest={false} />
                            <p className="text-xs text-gray-500">{data.file_count} files</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Recommendation */}
                  {benchRecommendation && !benchRecommendation.error && (
                    <div className="bg-gray-800/80 rounded-lg p-4 border border-emerald-700/50">
                      <div className="flex items-center gap-2 mb-2">
                        <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                        <h3 className="text-sm font-medium text-emerald-400">Recommendation</h3>
                      </div>
                      <p className="text-sm text-white mb-2">
                        Primary: <span className="text-emerald-400 font-medium">{benchRecommendation.primary_backend}</span>
                        {benchRecommendation.primary_wer != null && ` (WER: ${(benchRecommendation.primary_wer * 100).toFixed(2)}%)`}
                      </p>
                      {benchRecommendation.fallback_order?.length > 0 && (
                        <p className="text-xs text-gray-400 mb-3">
                          Fallback order: {benchRecommendation.fallback_order.join(" → ")}
                        </p>
                      )}
                      <Button onClick={handleApplyRecommendation} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                        <Settings2 className="w-4 h-4 mr-1" />
                        Apply Recommendation
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
